// Which consent regime applies to a visitor, by country.
//
// Analytics runs for every visitor by default. In jurisdictions that require
// prior opt-in before analytics storage may be set, the banner decision is
// authoritative instead and a rejection keeps analytics off. Advertising storage
// is consent-gated in BOTH regimes — the default covers analytics only.

export type ConsentRegime = 'strict' | 'analytics-always';

export interface ConsentPolicy {
  regime: ConsentRegime;
}

export const strictConsentPolicy: ConsentPolicy = { regime: 'strict' };
export const analyticsAlwaysConsentPolicy: ConsentPolicy = { regime: 'analytics-always' };

/**
 * Countries whose law requires prior consent for analytics storage: the EEA
 * (EU 27 plus Iceland, Liechtenstein, Norway), the UK, and Switzerland.
 * Everywhere else analytics is measured for every visitor. Add a country here
 * when its regulator adopts an opt-in rule.
 */
export const STRICT_CONSENT_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
] as const;

const STRICT_CONSENT_COUNTRY_SET: ReadonlySet<string> = new Set(STRICT_CONSENT_COUNTRIES);

/**
 * CloudFront uses ISO 3166-1 alpha-2 country codes. A missing or malformed
 * header means the country is unknown rather than non-strict, so it falls back
 * to the strict regime: a broken geo lookup then under-reports analytics
 * instead of tracking an EEA visitor who never consented.
 */
export function consentPolicyForCountryHeader(countryHeader: string | null): ConsentPolicy {
  const countryCode = countryHeader?.trim().toUpperCase();
  if (countryCode == null || !/^[A-Z]{2}$/.test(countryCode)) return strictConsentPolicy;

  return STRICT_CONSENT_COUNTRY_SET.has(countryCode)
    ? strictConsentPolicy
    : analyticsAlwaysConsentPolicy;
}

/**
 * Parses the policy endpoint's response. Anything unrecognised — a network
 * failure, an error page, a future response shape — degrades to strict, so
 * analytics is only ever granted on a positive, well-formed answer.
 */
export function parseConsentPolicy(value: unknown): ConsentPolicy {
  if (
    typeof value === 'object' &&
    value !== null &&
    'regime' in value &&
    value.regime === 'analytics-always'
  ) {
    return analyticsAlwaysConsentPolicy;
  }

  return strictConsentPolicy;
}
